import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Project, ProjectContextType } from '../types/project';
import { useProjectData } from '../hooks/useProjectData';
import { useProjectView } from '../hooks/useProjectView';
import { toast } from '@/components/ui/use-toast';
import Loading from '@/components/Loading';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { dataRefresh } from '@/lib/dataRefresh';
import { supabase } from '@/integrations/supabase/client';

// Re-export types
export type { Project, ProjectContextType };

export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function useProject(): ProjectContextType {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

export function ProjectProviderContent({ children }: { children: React.ReactNode }) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  
  const {
    projects,
    setProjects,
    loadProjects,
    syncWithDatabase,
    addProject,
    updateProject,
    getProject,
    deleteProject,
    deleteLocalOnlyProjects,
    saveProjectsToLocalStorage
  } = useProjectData();
  
  const { activeView, setActiveView } = useProjectView();
  
  // Save projects to localStorage whenever they change
  useEffect(() => {
    if (!projects) return; // Don't save if projects is null/undefined
    try {
      saveProjectsToLocalStorage();
    } catch (err) {
      console.error('Error saving to localStorage:', err);
      // Don't set error state for localStorage issues
    }
  }, [projects, saveProjectsToLocalStorage]);

  // Subscribe to Supabase Realtime for cross-browser/tab sync
  useEffect(() => {
    console.log('[ProjectContext] Setting up Supabase Realtime subscription for projects...');
    
    const channel = supabase
      .channel('projects-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events: INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'projects',
        },
        async (payload) => {
          console.log('[ProjectContext] Realtime event received:', payload.eventType, payload);
          
          // Reload projects on any change from the database
          // This ensures UI stays in sync across all browser tabs/windows
          try {
            await loadProjects(false); // Silent reload without toast
          } catch (error) {
            console.error('[ProjectContext] Error reloading projects from realtime event:', error);
          }
        }
      )
      .subscribe((status) => {
        console.log('[ProjectContext] Realtime subscription status:', status);
      });

    return () => {
      console.log('[ProjectContext] Cleaning up Supabase Realtime subscription...');
      supabase.removeChannel(channel);
    };
  }, [loadProjects]);

  // Subscribe to local data refresh events (for same-window updates)
  useEffect(() => {
    const unsubscribe = dataRefresh.subscribe('projects', async () => {
      console.log('[ProjectContext] Received local refresh trigger, reloading projects...');
      try {
        // Pass false to suppress toast on refresh (silent reload)
        await loadProjects(false);
      } catch (error) {
        console.error('[ProjectContext] Error reloading projects:', error);
      }
    });

    return unsubscribe;
  }, [loadProjects]);

  // Initialize projects
  useEffect(() => {
    const initializeProjects = async () => {
      if (hasAttemptedLoad) return;
      setHasAttemptedLoad(true);

      try {
        // Load from database first
        const loadedProjects = await loadProjects();
        
        if (Array.isArray(loadedProjects)) {
          console.log('Setting projects from database:', loadedProjects);
          setProjects(loadedProjects);
        } else {
          console.warn('Database projects is not an array:', loadedProjects);
          // If no database projects, try localStorage
          const savedProjects = localStorage.getItem('construction-app-projects');
          if (savedProjects) {
            try {
              const localProjects = JSON.parse(savedProjects);
              if (Array.isArray(localProjects)) {
                console.log('Setting projects from localStorage:', localProjects);
                setProjects(localProjects);
              } else {
                console.warn('Local projects is not an array:', localProjects);
                setProjects([]);
              }
            } catch (parseErr) {
              console.error('Error parsing local projects:', parseErr);
              setProjects([]);
            }
          } else {
            console.log('No projects found in localStorage, initializing empty array');
            setProjects([]);
          }
        }
      } catch (err) {
        console.error('Error loading projects:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setProjects([]);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeProjects();
  }, [loadProjects, setProjects, hasAttemptedLoad]);

  if (error) {
    // Ensure we always throw a proper Error object
    throw error instanceof Error ? error : new Error(String(error));
  }

  if (isInitializing) {
    return <Loading />;
  }

  // Initialize context value
  const value: ProjectContextType = {
    projects: Array.isArray(projects) ? projects : [],
    setProjects,
    addProject,
    updateProject,
    getProject,
    deleteProject,
    activeView,
    setActiveView,
    deleteLocalOnlyProjects,
    syncWithDatabase
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <ProjectProviderContent>{children}</ProjectProviderContent>
    </ErrorBoundary>
  );
}
